import Vue from 'vue';
import _ from 'lodash';
import {
  configs,
  updateConfigs,
  orderTimers,
  countries as fetchCountries,
  regions as fetchRegions,
  districts as fetchDistricts,
  cemeteries as fetchCemeteries,
  workingRegions,
  setWorkingRegions,
  fetchAdmins,
  updatePermissions,
  individualRegions,
  updateOrderTimers,
} from '@/api/settings';
import {
  SET_CONFIGS,
  SET_ORDER_TIMERS,
  SET_COUNTRIES,
  SET_REGIONS,
  SET_DISTRICTS,
  SET_CEMETERIES,
  SET_WORKING_REGIONS,
  UPDATE_WORKING_REGION,
  SET_ADMINS,
  UPDATE_ADMIN_PERMISSION,
  SET_INDIVIDUAL_REGIONS,
} from './mutation-types';

export default {
  state: {
    configs: {},
    orderTimers: {},
    countries: [],
    regions: {},
    districts: {},
    cemeteries: {},
    workingRegions: [],
    admins: [],
    enums: {
      legalType: [
        { key: 'llc', label: 'ООО' },
        { key: 'sole', label: 'ИП' },
        { key: 'individual', label: 'Самозанятый' },
      ],
      taxSystem: [
        { key: 'vat_osn', label: 'Плательщик НДС ОСН 20%' },
        { key: 'usn_6', label: 'Неплательщик НДС УСН 6%' },
        { key: 'usn_15', label: 'Неплательщик НДС УСН 15%' },
        { key: 'other', label: 'Другая' },
      ],
      fence: [
        { key: null, label: 'Нет' },
        { key: 'simple', label: 'Простая (прямая)' },
        { key: 'patterned', label: 'Витая (узорчатая)' },
      ],
      headstone: [
        { key: null, label: 'Нет' },
        { key: 'nameplate', label: 'Табличка' },
        { key: 'metal_cross', label: 'Крест металл' },
        { key: 'wooden_cross', label: 'Крест дерево' },
        { key: 'granite_monument', label: 'Памятник гранит' },
        { key: 'marble_monument', label: 'Памятник мрамор' },
        { key: 'metal_monument', label: 'Памятник металл' },
        { key: 'statue', label: 'Статуя' },
        { key: 'memorial', label: 'Мемориал' },
      ],
      size: [
        { key: 'small', label: 'Малый (до 2м<sup>2</sup>)' },
        { key: 'medium', label: 'Средний (от 3м<sup>2</sup>)' },
        { key: 'large', label: 'Большой (более 3м<sup>2</sup>)' },
      ],
      pedestal: [
        { key: null, label: 'Нет' },
        { key: 'concrete', label: 'Цоколь бетон' },
        { key: 'granite', label: 'Цоколь гранит' },
        { key: 'marble', label: 'Цоколь мрамор' },
        { key: 'metal', label: 'Цоколь металл' },
      ],
      flowerGarden: [
        { key: null, label: 'Нет' },
        { key: 'unfenced', label: 'Неогороженный цветник' },
        { key: 'concrete', label: 'Бетонный' },
        { key: 'granite', label: 'Гранитный' },
        { key: 'marble', label: 'Мраморный' },
        { key: 'metal', label: 'Металлический' },
      ],
    },
    individualRegions: [],
  },
  getters: {
    regions: (state) => (id) => {
      if (typeof id !== 'undefined') {
        return _.get(state.regions, id) || [];
      }
      return state.regions;
    },
    districts: (state) => (id) => {
      if (typeof id !== 'undefined') {
        return _.get(state.districts, id) || [];
      }
      return state.districts;
    },
    cemeteries: (state) => (id) => {
      if (typeof id !== 'undefined') {
        return _.get(state.cemeteries, id) || [];
      }
      return state.cemeteries;
    },
    enums: (state) => (id) => {
      if (typeof id !== 'undefined') {
        return _.get(state.enums, id) || [];
      }
      return state.enums;
    },
    selectEnums: (state, getters) => (id) => {
      if (typeof id !== 'undefined') {
        return getters.enums(id).map((obj) => ({
          value: obj.key,
          label: obj.label,
        }));
      }
      return [];
    },
  },
  actions: {
    ACT_FETCH_CONFIGS: async ({ commit, dispatch }) => {
      dispatch('wait/start', 'fetch.configs', { root: true });
      return configs().then((response) => {
        if (response.data) {
          commit(SET_CONFIGS, response.data);
          return Promise.resolve(response.data);
        }
        return Promise.reject();
      }).finally(() => {
        dispatch('wait/end', 'fetch.configs', { root: true });
      });
    },
    ACT_UPDATE_CONFIGS: async ({ commit, dispatch }, data) => {
      dispatch('wait/start', 'update.configs', { root: true });
      return updateConfigs(data).then((response) => {
        if (response.status === 200) {
          commit(SET_CONFIGS, data);
        }
      }).finally(() => {
        dispatch('wait/end', 'update.configs', { root: true });
      });
    },
    ACT_FETCH_ORDER_TIMERS: async ({ commit, dispatch }) => {
      dispatch('wait/start', 'order.timers.fetch', { root: true });
      return orderTimers().then((response) => {
        if (response.data) {
          commit(SET_ORDER_TIMERS, response.data);
          return Promise.resolve(response.data);
        }
        return Promise.reject();
      }).finally(() => {
        dispatch('wait/end', 'order.timers.fetch', { root: true });
      });
    },
    ACT_UPDATE_ORDER_TIMERS: async ({
      commit,
      dispatch,
      rootGetters: getters,
    }, payload) => {
      if (getters['wait/is']('order.timers.update')) {
        return Promise.reject();
      }
      dispatch('wait/start', 'order.timers.update', { root: true });
      return updateOrderTimers(payload).then(() => {
        commit(SET_ORDER_TIMERS, payload);
      }).finally(() => {
        dispatch('wait/end', 'order.timers.update', { root: true });
      });
    },
    ACT_FETCH_COUNTRIES: async ({ commit, dispatch }) => {
      dispatch('wait/start', 'fetch.countries', { root: true });
      return fetchCountries().then((response) => {
        if (response.data) {
          commit(SET_COUNTRIES, response.data);
        }
      }).finally(() => {
        dispatch('wait/end', 'fetch.countries', { root: true });
      });
    },
    ACT_FETCH_REGIONS: async ({
      commit,
      dispatch,
      state,
    }, id) => {
      if (_.has(state.regions, id)) {
        return Promise.resolve(_.get(state.regions, id));
      }
      dispatch('wait/start', `fetch.regions.${id}`, { root: true });
      return fetchRegions(id).then((response) => {
        if (response.data) {
          commit(SET_REGIONS, {
            id,
            data: response.data,
          });
          return Promise.resolve(response.data);
        }
        return Promise.reject();
      }).finally(() => {
        dispatch('wait/end', `fetch.regions.${id}`, { root: true });
      });
    },
    ACT_FETCH_DISTRICTS: async ({
      commit,
      dispatch,
      state,
      rootGetters: getters,
    }, id) => {
      if (_.has(state.districts, id) || getters['wait/is'](`fetch.districts.${id}`)) {
        return Promise.resolve(_.get(state.districts, id));
      }
      dispatch('wait/start', `fetch.districts.${id}`, { root: true });
      return fetchDistricts(id).then((response) => {
        if (response.data) {
          commit(SET_DISTRICTS, {
            id,
            data: response.data,
          });
          return Promise.resolve(response.data);
        }
        return Promise.reject();
      }).finally(() => {
        dispatch('wait/end', `fetch.districts.${id}`, { root: true });
      });
    },
    ACT_FETCH_CEMETERIES: async ({
      commit,
      dispatch,
      state,
      rootGetters: getters,
    }, id) => {
      if (_.has(state.cemeteries, id) || getters['wait/is'](`fetch.cemeteries.${id}`)) {
        return Promise.resolve(_.get(state.cemeteries, id));
      }
      dispatch('wait/start', `fetch.cemeteries.${id}`, { root: true });
      return fetchCemeteries(id).then((response) => {
        if (response.data) {
          commit(SET_CEMETERIES, {
            id,
            data: response.data,
          });
        }
      }).finally(() => {
        dispatch('wait/end', `fetch.cemeteries.${id}`, { root: true });
      });
    },
    ACT_FETCH_WORKING_REGIONS: async ({ commit, dispatch }) => {
      dispatch('wait/start', 'fetch.working.regions', { root: true });
      return workingRegions().then((response) => {
        if (response.data) {
          commit(SET_WORKING_REGIONS, response.data);
        }
      }).finally(() => {
        dispatch('wait/end', 'fetch.working.regions', { root: true });
      });
    },
    ACT_UPDATE_WORKING_REGIONS: async ({
      commit,
      dispatch,
      rootGetters: getters,
    }, {
        id,
        value,
      }) => {
      if (getters['wait/is'](`update.working.regions.${id}`)) {
        return Promise.reject();
      }
      dispatch('wait/start', `update.working.regions.${id}`, { root: true });
      return setWorkingRegions(id, value).then((response) => {
        if (response.status === 200) {
          commit(UPDATE_WORKING_REGION, {
            id,
            value,
          });
        }
      }).finally(() => {
        dispatch('wait/end', `update.working.regions.${id}`, { root: true });
      });
    },
    ACT_FETCH_ADMINS: async ({ commit, dispatch }) => {
      dispatch('wait/start', 'fetch.admins', { root: true });
      return fetchAdmins().then((response) => {
        if (response.data) {
          commit(SET_ADMINS, response.data);
        }
      }).finally(() => {
        dispatch('wait/end', 'fetch.admins', { root: true });
      });
    },
    ACT_UPDATE_ADMIN_PERMISSIONS: async ({ commit, dispatch }, {
      id,
      permissions,
    }) => {
      dispatch('wait/start', `update.permissions.${id}`, { root: true });
      return updatePermissions(id, permissions).then((response) => {
        if (response.status === 200) {
          commit(UPDATE_ADMIN_PERMISSION, {
            id,
            permissions,
          });
        }
      }).finally(() => {
        dispatch('wait/end', `update.permissions.${id}`, { root: true });
      });
    },
    ACT_FETCH_INDIVIDUAL_REGIONS: ({ commit }) => individualRegions().then((response) => {
      if (response.data) {
        commit(SET_INDIVIDUAL_REGIONS, response.data);
      }
    }),
  },
  mutations: {
    [SET_CONFIGS](state, data) {
      state.configs = {
        ...state.configs,
        ...data,
      };
    },
    [SET_ORDER_TIMERS](state, data) {
      state.orderTimers = {
        ...state.orderTimers,
        ...data,
      };
    },
    [SET_COUNTRIES](state, data) {
      state.countries = data;
    },
    [SET_REGIONS](state, { id, data }) {
      Vue.set(state.regions, id, data);
    },
    [SET_DISTRICTS](state, { id, data }) {
      Vue.set(state.districts, id, data);
    },
    [SET_CEMETERIES](state, { id, data }) {
      Vue.set(state.cemeteries, id, data);
    },
    [SET_WORKING_REGIONS](state, data) {
      state.workingRegions = data;
    },
    [UPDATE_WORKING_REGION](state, { id, value }) {
      state.workingRegions = state.workingRegions.map((obj) => {
        if (obj.id === id) {
          return {
            ...obj,
            active: value,
          };
        }
        return obj;
      });
    },
    [SET_ADMINS](state, data) {
      state.admins = data;
    },
    [UPDATE_ADMIN_PERMISSION](state, { id, permissions }) {
      state.admins = state.admins.map((admin) => {
        if (parseInt(admin.id, 10) === parseInt(id, 10)) {
          return {
            ...admin,
            permissions: {
              ...admin.permissions,
              ...permissions,
            },
          };
        }
        return admin;
      });
    },
    [SET_INDIVIDUAL_REGIONS](state, data) {
      state.individualRegions = data;
    },
  },
  namespaced: true,
};
